import { Box, Button, Grid, IconButton, Modal, Tooltip, Typography } from "@material-ui/core";
import { FC, useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import FormikForm, { FormikStep } from "src/components/ui-components/FormikForm";
import { Description, Info } from "@material-ui/icons";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { Field } from "formik";

dayjs.extend(utc);

type ModalProps = {
  open: boolean;
  handleClose: () => void;
  FromDateDifference?: number | null;
  handleSubmit: (values: any) => any;
};

const ReportModal: FC<ModalProps> = ({ open, handleClose, FromDateDifference, handleSubmit }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const formRef = useRef<any>(null);

  const defaultForm = {
    FromDate: dayjs().utc().subtract(FromDateDifference ?? 7, "days").format(),
    ToDate: dayjs().utc().format(),
  };

  const resetFormData = (data: any) => {
    if (formRef?.current) {
      formRef.current.resetForm({
        values: data,
      });
    }
  };

  const handleDownloadClick = async (values: any) => {
    setIsLoading(true);
    await handleSubmit({ FromDate: dayjs(values.FromDate).utc().startOf("day").format(), ToDate: dayjs(values.ToDate).utc().endOf("day").format()});
    setIsLoading(false);
  };

  useEffect(() => {
    const setDefaultDates = () => {
      const defaultDatesForForm = {
        FromDate: dayjs().utc().subtract(FromDateDifference ?? 7, "days").startOf("day").format(),
        ToDate: dayjs().utc().endOf("day").format(),
      };
  
      resetFormData(defaultDatesForForm);
    };
  
    if (open && formRef && formRef.current) {
      setDefaultDates();
    }
  }, [open, FromDateDifference]);
  

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="Generate Report"
      aria-describedby="Generate User Login Report"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          width: "70%",
        }}
      >
        <Grid container justifyContent="space-around">
          <Grid item xs={6} sm={6} md={6} lg={6} xl={6} display="flex" justifyContent="flex-start">
            <Typography sx={{ marginBottom: "2%", fontWeight: "bold" }} variant="h5">
              User Login Report
            </Typography>
          </Grid>
          <Grid item xs={6} sm={6} md={6} lg={6} xl={6} display="flex" justifyContent="flex-end">
            <Tooltip placement="left" title="This report includes details of users who have logged in, along with their login timestamps. This is not an export of users data, and will not include details of users who have not logged in within the given date range." arrow>
              <IconButton>
                <Info color="primary" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        <FormikForm
          innerRef={formRef}
          initialValues={defaultForm}
          validateOnChange
          onSubmit={async (values): Promise<void> => {
            await handleDownloadClick(values);
          }}
        >
          <FormikStep
            validationSchema={Yup.object({
              FromDate: Yup.date()
                .required("From Date is required")
                .max(dayjs().utc().toDate(), "Cannot be a future date"),
              ToDate: Yup.date()
                .required("To Date is required")
                .max(dayjs().utc().toDate(), "Cannot be a future date")
                .min(
                  Yup.ref('FromDate'),
                  'To Date cannot be before From date'
                ),
            })}
          >
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <Field name="FromDate">
                      {({ form }: any) => (
                        <KeyboardDatePicker
                          disableToolbar
                          variant="inline"
                          format="dd/MM/yyyy"
                          views={["year", "month", "date"]}
                          maxDate={form.values.ToDate ? form.values.ToDate : new Date()}
                          margin="normal"
                          id="from-date-picker"
                          label="From Date *"
                          value={form.values.FromDate ? dayjs(form.values.FromDate).toDate() : null}
                          onChange={(date) => {
                            form.setFieldValue("FromDate", date ? dayjs(date).utc().format() : "");
                            form.setFieldTouched("FromDate", true, false);
                          }}
                          fullWidth
                          error={Boolean(form.touched.FromDate && form.errors.FromDate)}
                          helperText={form.touched.FromDate && form.errors.FromDate ? form.errors.FromDate : ""}
                          InputProps={{ readOnly: true }}
                          shouldDisableDate={(date) =>
                            date > dayjs(form.values.ToDate || new Date()).endOf("day")
                          }
                        />
                      )}
                    </Field>
                  </MuiPickersUtilsProvider>
                </Grid> 
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <Field name="ToDate">
                      {({ form }: any) => (
                        <KeyboardDatePicker
                          disableToolbar
                          variant="inline"
                          format="dd/MM/yyyy"
                          views={["year", "month", "date"]}
                          maxDate={new Date()}
                          minDate={form.values.FromDate}
                          margin="normal"
                          label="To Date *"
                          value={form.values.ToDate ? dayjs(form.values.ToDate).toDate() : null}
                          onChange={(date) => {
                            form.setFieldValue("ToDate", date ? dayjs(date).utc().format() : "")
                            form.setFieldTouched("ToDate", true, false);
                          }}
                          fullWidth
                          error={Boolean(form.touched.ToDate && form.errors.ToDate)}
                          helperText={form.touched.ToDate && form.errors.ToDate ? form.errors.ToDate : ""}
                          InputProps={{ readOnly: true }}
                          shouldDisableDate={(date) =>
                            date < dayjs(form.values.FromDate).startOf('day') || date > dayjs(new Date()).endOf("day")
                          }
                        />
                      )}
                    </Field>
                  </MuiPickersUtilsProvider>
                </Grid>
              </Grid>
              <Grid container spacing={2} justifyContent="end" style={{ marginTop: "2%" }}>
                <Button
                  sx={{
                    minWidth: 100,
                    marginRight: "2%",
                    color: "rgb(107, 119, 140)",
                  }}
                  disabled={isLoading}
                  variant="outlined"
                  type="button"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  sx={{
                    background: "linear-gradient(to bottom, #072f40, #072f40)",
                    minWidth: 100,
                  }}
                  disabled={isLoading}
                  type="submit"
                  variant="contained"
                >
                  <Description style={{ marginRight: "8px" }} />
                  Download
                </Button>
              </Grid>
            </>
          </FormikStep>
        </FormikForm>
      </Box>
    </Modal>
  );
};

export default ReportModal;
